import { useState } from "react";
import { auth, db } from "../firebase/firebase";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  FacebookAuthProvider,
  GithubAuthProvider,
  TwitterAuthProvider,
  signInWithPopup,
  updateProfile,
  sendPasswordResetEmail
} from "firebase/auth";
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from "firebase/firestore";

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isLoginView, setIsLoginView] = useState(true);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [showResetSentPopup, setShowResetSentPopup] = useState(false);
  const googleProvider = new GoogleAuthProvider();
  const facebookProvider = new FacebookAuthProvider();
  const twitterProvider = new TwitterAuthProvider();
  const githubProvider = new GithubAuthProvider();

  // 🔐 LOGIN
  const login = async () => {
    setError("");
    setLoading(true);

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      await checkAndResetPremiumStatus(userCredential.user);
    } catch (err) {
      setError(err.message);
    }

    setLoading(false);
  };

  // 📝 REGISTER
  const register = async () => {
    setError("");
    setLoading(true);

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      await updateProfile(user, {
        displayName: fullName
      });

      await setDoc(doc(db, "users", user.uid), {
        isPremium: false,
        name: fullName,
        email: email,
        premiumDuration: 0,
        premiumFrom: serverTimestamp()
      });

      setIsLoginView(true);
      setEmail("");
      setPassword("");
      setFullName("");
    } catch (err) {
      setError(err.message);
    }

    setLoading(false);
  };

  // 🔵 GOOGLE SIGN-IN
  const signInWithGoogle = async () => {
    setError("");
    setLoading(true);

    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;

      const docRef = doc(db, "users", user.uid);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        await setDoc(docRef, {
          isPremium: false,
          name: user.displayName || "",
          premiumDuration: 0,
          premiumFrom: serverTimestamp()
        });
      } else {
        await checkAndResetPremiumStatus(user);
      }
    } catch (err) {
      setError(err.message);
    }

    setLoading(false);
  };

  // 📘 FACEBOOK SIGN-IN
  const signInWithFacebook = async () => {
    setError("");
    setLoading(true);

    try {
      const result = await signInWithPopup(auth, facebookProvider);
      const user = result.user;

      const docRef = doc(db, "users", user.uid);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        await setDoc(docRef, {
          isPremium: false,
          name: user.displayName || "",
          premiumDuration: 0,
          premiumFrom: serverTimestamp()
        });
      } else {
        await checkAndResetPremiumStatus(user);
      }
    } catch (err) {
      setError(err.message);
    }

    setLoading(false);
  };

  //  𝕏 TWITTER SIGN-IN
  const signInWithTwitter = async () => {
    setError("");
    setLoading(true);

    try {
      const result = await signInWithPopup(auth, twitterProvider);
      const user = result.user;

      const docRef = doc(db, "users", user.uid);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        await setDoc(docRef, {
          isPremium: false,
          name: user.displayName || "",
          premiumDuration: 0,
          premiumFrom: serverTimestamp()
        });
      } else {
        await checkAndResetPremiumStatus(user);
      }
    } catch (err) {
      setError(err.message);
    }

    setLoading(false);
  };

  // 🐙 GITHUB SIGN-IN
  const signInWithGithub = async () => {
    setError("");
    setLoading(true);

    try {
      const result = await signInWithPopup(auth, githubProvider);
      const user = result.user;

      const docRef = doc(db, "users", user.uid);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        await setDoc(docRef, {
          isPremium: false,
          name: user.displayName || "",
          premiumDuration: 0,
          premiumFrom: serverTimestamp()
        });
      } else {
        await checkAndResetPremiumStatus(user);
      }
    } catch (err) {
      setError(err.message);
    }

    setLoading(false);
  };

  // 🔍 CHECK AND RESET PREMIUM STATUS
  const checkAndResetPremiumStatus = async (user) => {
    try {
      const docRef = doc(db, "users", user.uid);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const userData = docSnap.data();

        if (userData.isPremium && userData.premiumFrom && userData.premiumDuration) {
          const premiumFrom = userData.premiumFrom.toDate ? userData.premiumFrom.toDate() : new Date(userData.premiumFrom);
          const premiumUntil = new Date(premiumFrom.getTime() + userData.premiumDuration * 24 * 60 * 60 * 1000);
          const currentDate = new Date();

          if (currentDate > premiumUntil) {
            await updateDoc(docRef, {
              isPremium: false
            });
          }
        }
      }
    } catch (err) {
      console.error("Error checking premium status:", err);
    }
  };

  // ⌨️ ENTER = LOGIN
  const handleSubmit = async (e) => {
    e.preventDefault();
    await login();
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    await register();
  };

  const toggleView = () => {
    setError("");
    setIsLoginView(!isLoginView);
    setEmail("");
    setPassword("");
    setFullName("");
  };

  const handlePasswordReset = async () => {
    if (!resetEmail) {
      setError("Please enter your email address");
      return;
    }

    try {
      await sendPasswordResetEmail(auth, resetEmail);
      setShowResetModal(false);
      setShowResetSentPopup(true);
      setResetEmail("");
      setError("");
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background: "#f5f7fa"
      }}
    >
      {isLoginView ? (
        <form
          onSubmit={handleSubmit}
          style={{
            background: "white",
            padding: "30px",
            borderRadius: "12px",
            width: "320px",
            boxShadow: "0 10px 25px rgba(0,0,0,0.1)"
          }}
        >
          <img
            src="/img/logo1-140x140.png"
            alt="Logo"
            style={{
              display: "block",
              margin: "0 auto 15px auto",
              width: "140px",
              height: "140px"
            }}
          />
          <p style={{ textAlign: "center", marginBottom: "20px", color: "#666" }}>
            Please login/register to access the prep exams
          </p>
          <h2 style={{ marginBottom: "20px", textAlign: "center" }}>
            Login
          </h2>

          {/* EMAIL */}
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            style={{
              width: "100%",
              padding: "10px",
              marginBottom: "15px",
              borderRadius: "8px",
              border: "1px solid #ccc"
            }}
          />

          {/* PASSWORD */}
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            style={{
              width: "100%",
              padding: "10px",
              marginBottom: "15px",
              borderRadius: "8px",
              border: "1px solid #ccc"
            }}
          />

          {/* ERROR */}
          {error && (
            <p style={{ color: "red", fontSize: "14px" }}>
              {error}
            </p>
          )}

          {/* LOGIN BUTTON */}
          <button
            type="submit"
            disabled={loading}
            style={{
              width: "100%",
              padding: "10px",
              background: "#007bff",
              color: "white",
              border: "none",
              borderRadius: "8px",
              cursor: loading ? "not-allowed" : "pointer",
              marginBottom: "10px"
            }}
          >
            {loading ? "Logging in..." : "Login"}
          </button>

          {/* REGISTER LINK */}
          <p style={{ textAlign: "center", fontSize: "14px", color: "#666", marginTop: "5px", marginBottom: "10px" }}>
            Don't have an account?{" "}
            <span
              onClick={toggleView}
              style={{
                color: "#007bff",
                cursor: "pointer",
                textDecoration: "underline"
              }}
            >
              Register
            </span>
          </p>

          {/* FORGOT PASSWORD LINK */}
          <p style={{ textAlign: "center", fontSize: "14px", color: "#666", marginBottom: "20px" }}>
            Forgot your password?{" "}
            <span
              onClick={() => setShowResetModal(true)}
              style={{
                color: "#007bff",
                cursor: "pointer",
                textDecoration: "underline"
              }}
            >
              Reset
            </span>
          </p>

          {/* GOOGLE SIGN-IN BUTTON */}
          <button
            type="button"
            onClick={signInWithGoogle}
            disabled={loading}
            style={{
              width: "100%",
              padding: "10px",
              background: "#fff",
              color: "#333",
              border: "1px solid #ddd",
              borderRadius: "8px",
              cursor: loading ? "not-allowed" : "pointer",
              marginBottom: "5px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "10px"
            }}
          >
            <img
              src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
              alt="Google"
              style={{ width: "18px", height: "18px" }}
            />
            {loading ? "Signing in..." : "Sign in with Google"}
          </button>

          {/* FACEBOOK SIGN-IN BUTTON */}
          <button
            type="button"
            onClick={signInWithFacebook}
            disabled={loading}
            style={{
              width: "100%",
              padding: "10px",
              background: "#fff",
              color: "#333",
              border: "1px solid #ddd",
              borderRadius: "8px",
              cursor: loading ? "not-allowed" : "pointer",
              marginBottom: "5px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "10px"
            }}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="#1877f2"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
            {loading ? "Signing in..." : "Sign in with Facebook"}
          </button>

          {/* X (TWITTER) SIGN-IN BUTTON */}
          <button
            type="button"
            onClick={signInWithTwitter}
            disabled={loading}
            style={{
              width: "100%",
              padding: "10px",
              background: "#fff",
              color: "#333",
              border: "1px solid #ddd",
              borderRadius: "8px",
              cursor: loading ? "not-allowed" : "pointer",
              marginBottom: "5px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "10px"
            }}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="#000"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
            </svg>
            {loading ? "Signing in..." : "Sign in with X"}
          </button>

          {/* GITHUB SIGN-IN BUTTON */}
          <button
            type="button"
            onClick={signInWithGithub}
            disabled={loading}
            style={{
              width: "100%",
              padding: "10px",
              background: "#fff",
              color: "#333",
              border: "1px solid #ddd",
              borderRadius: "8px",
              cursor: loading ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "10px"
            }}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="#333"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
            </svg>
            {loading ? "Signing in..." : "Sign in with Github"}
          </button>
        </form>
      ) : (
        <form
          onSubmit={handleRegisterSubmit}
          style={{
            background: "white",
            padding: "30px",
            borderRadius: "12px",
            width: "320px",
            boxShadow: "0 10px 25px rgba(0,0,0,0.1)"
          }}
        >
          <img
            src="/img/logo1-140x140.png"
            alt="Logo"
            style={{
              display: "block",
              margin: "0 auto 15px auto",
              width: "140px",
              height: "140px"
            }}
          />
          <h2 style={{ marginBottom: "20px", textAlign: "center" }}>
            Register
          </h2>

          {/* FULL NAME */}
          <input
            type="text"
            placeholder="Full Name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            required
            style={{
              width: "100%",
              padding: "10px",
              marginBottom: "15px",
              borderRadius: "8px",
              border: "1px solid #ccc"
            }}
          />

          {/* EMAIL */}
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            style={{
              width: "100%",
              padding: "10px",
              marginBottom: "15px",
              borderRadius: "8px",
              border: "1px solid #ccc"
            }}
          />

          {/* PASSWORD */}
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            style={{
              width: "100%",
              padding: "10px",
              marginBottom: "15px",
              borderRadius: "8px",
              border: "1px solid #ccc"
            }}
          />

          {/* ERROR */}
          {error && (
            <p style={{ color: "red", fontSize: "14px" }}>
              {error}
            </p>
          )}

          {/* REGISTER BUTTON */}
          <button
            type="submit"
            disabled={loading}
            style={{
              width: "100%",
              padding: "10px",
              background: "#28a745",
              color: "white",
              border: "none",
              borderRadius: "8px",
              cursor: loading ? "not-allowed" : "pointer",
              marginBottom: "20px"
            }}
          >
            {loading ? "Registering..." : "Register"}
          </button>

          {/* BACK TO LOGIN LINK */}
          <p style={{ textAlign: "center", color: "#666" }}>
            Already have an account?{" "}
            <span
              onClick={toggleView}
              style={{
                color: "#007bff",
                cursor: "pointer",
                textDecoration: "underline"
              }}
            >
              Login
            </span>
          </p>
        </form>
      )}

      {/* PASSWORD RESET MODAL */}
      {showResetModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000
          }}
        >
          <div
            style={{
              background: "white",
              padding: "30px",
              borderRadius: "12px",
              width: "350px",
              boxShadow: "0 10px 25px rgba(0,0,0,0.2)"
            }}
          >
            <h3 style={{ marginBottom: "20px", textAlign: "center" }}>
              Reset Password
            </h3>
            <p style={{ marginBottom: "15px", color: "#666", fontSize: "14px" }}>
              Enter your email address and we'll send you a link to reset your password.
            </p>
            <input
              type="email"
              placeholder="Email"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              required
              style={{
                width: "100%",
                padding: "10px",
                marginBottom: "15px",
                borderRadius: "8px",
                border: "1px solid #ccc"
              }}
            />
            {error && (
              <p style={{ color: "red", fontSize: "14px", marginBottom: "15px" }}>
                {error}
              </p>
            )}
            <div style={{ display: "flex", gap: "10px" }}>
              <button
                type="button"
                onClick={() => {
                  setShowResetModal(false);
                  setError("");
                  setResetEmail("");
                }}
                style={{
                  flex: 1,
                  padding: "10px",
                  background: "#6c757d",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer"
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handlePasswordReset}
                style={{
                  flex: 1,
                  padding: "10px",
                  background: "#007bff",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer"
                }}
              >
                Send Reset Link
              </button>
            </div>
          </div>
        </div>
      )}

      {/* RESET SENT POPUP */}
      {showResetSentPopup && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000
          }}
        >
          <div
            style={{
              background: "white",
              padding: "30px",
              borderRadius: "12px",
              width: "350px",
              boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
              textAlign: "center"
            }}
          >
            <div
              style={{
                width: "60px",
                height: "60px",
                background: "#28a745",
                borderRadius: "50%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                margin: "0 auto 20px auto"
              }}
            >
              <svg
                width="30"
                height="30"
                viewBox="0 0 24 24"
                fill="white"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
              </svg>
            </div>
            <h3 style={{ marginBottom: "15px", color: "#28a745" }}>
              Reset Email Sent!
            </h3>
            <p style={{ marginBottom: "25px", color: "#666" }}>
              A password reset link has been sent to your email. Please check your inbox and follow the instructions to reset your password.
            </p>
            <button
              type="button"
              onClick={() => setShowResetSentPopup(false)}
              style={{
                width: "100%",
                padding: "10px",
                background: "#28a745",
                color: "white",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer"
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Login;
